import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import adminService from '../../services/adminService';
import studentService from '../../services/studentService';

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const notificationRef = useRef(null);

    // Fetch notifications only when dropdown is opened
    useEffect(() => {
        if (user && showNotifications) {
            fetchNotifications();
        }
    }, [user, showNotifications]);

    // Close notification dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close mobile menu when route changes
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const fetchNotifications = async () => {
        try {
            // Use admin or student service based on user role
            const service = user?.role === 'admin' ? adminService : studentService;
            const response = await service.getNotifications();
            if (response.success) {
                setNotifications(response.data.notifications || []);
                setUnreadCount(response.data.unread_count || 0);
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            const service = user?.role === 'admin' ? adminService : studentService;
            await service.markAllNotificationsRead();
            fetchNotifications();
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    const handleNotificationClick = async (notification) => {
        try {
            const service = user?.role === 'admin' ? adminService : studentService;
            if (!notification.read) {
                await service.markNotificationRead(notification.id);
                fetchNotifications();
            }
            if (notification.link) {
                navigate(notification.link);
                setShowNotifications(false);
            }
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return 'Just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
        return date.toLocaleDateString();
    };

    // Check if link is active
    const isActive = (path) => {
        return location.pathname === path;
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <header className="sticky top-0 z-50 w-full bg-white dark:bg-background-dark border-b border-[#dfe0e2] dark:border-gray-800">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-16">
                {/* Logo and Brand */}
                <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    <img
                        src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c0/COMSATS_new_logo.jpg/770px-COMSATS_new_logo.jpg"
                        alt="COMSATS Logo"
                        className="h-7 sm:h-9 w-auto object-contain"
                    />
                    <h2 className="text-[#131416] dark:text-white text-sm sm:text-base lg:text-lg font-semibold tracking-tight leading-tight">
                        COMSATS Past Papers
                    </h2>
                </Link>

                {/* Desktop Navigation Links */}
                <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
                    {user ? (
                        <>
                            <Link
                                to="/dashboard"
                                className={`text-sm ${isActive('/dashboard') ? 'font-bold text-primary' : 'font-medium text-gray-700 dark:text-gray-300'} hover:text-primary transition-colors flex items-center gap-1`}
                            >
                                <span className="material-symbols-outlined text-[18px]">dashboard</span>
                                Dashboard
                            </Link>
                            <Link
                                to="/browse"
                                className={`text-sm ${isActive('/browse') ? 'font-bold text-primary' : 'font-medium text-gray-700 dark:text-gray-300'} hover:text-primary transition-colors`}
                            >
                                Browse Papers
                            </Link>
                            <Link
                                to="/upload"
                                className={`text-sm ${isActive('/upload') ? 'font-bold text-primary' : 'font-medium text-gray-700 dark:text-gray-300'} hover:text-primary transition-colors`}
                            >
                                Upload
                            </Link>
                            <Link
                                to="/my-uploads"
                                className={`text-sm ${isActive('/my-uploads') ? 'font-bold text-primary' : 'font-medium text-gray-700 dark:text-gray-300'} hover:text-primary transition-colors`}
                            >
                                My Uploads
                            </Link>
                            <Link
                                to="/backpack"
                                className={`text-sm ${isActive('/backpack') ? 'font-bold text-primary' : 'font-medium text-gray-700 dark:text-gray-300'} hover:text-primary transition-colors flex items-center gap-1`}
                            >
                                <span className="material-symbols-outlined text-[18px]">backpack</span>
                                Backpack
                            </Link>
                            {user.role === 'admin' && (
                                <Link
                                    to="/admin/dashboard"
                                    className={`text-sm ${location.pathname.startsWith('/admin') ? 'font-bold text-primary' : 'font-medium text-gray-700 dark:text-gray-300'} hover:text-primary transition-colors`}
                                >
                                    Admin
                                </Link>
                            )}
                        </>
                    ) : (
                        <>
                            <Link
                                to="/about"
                                className={`text-sm ${isActive('/about') ? 'font-bold text-primary' : 'font-medium text-gray-700 dark:text-gray-300'} hover:text-primary transition-colors`}
                            >
                                About
                            </Link>
                        </>
                    )}
                </nav>

                {/* Right Side - Auth Buttons & Actions */}
                <div className="flex items-center gap-2 sm:gap-3">
                    {user ? (
                        <>
                            {/* Notification Bell */}
                            <div className="relative" ref={notificationRef}>
                                <button
                                    onClick={() => setShowNotifications(!showNotifications)}
                                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative"
                                >
                                    <span className="material-symbols-outlined text-gray-600 dark:text-gray-300 text-xl sm:text-2xl">
                                        notifications
                                    </span>
                                    {unreadCount > 0 && (
                                        <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1">
                                            {unreadCount > 9 ? '9+' : unreadCount}
                                        </span>
                                    )}
                                </button>

                                {/* Notification Dropdown */}
                                {showNotifications && (
                                    <div className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 mt-2 sm:w-96 max-w-md bg-white dark:bg-[#1c2126] rounded-xl shadow-2xl border border-[#dfe0e2] dark:border-gray-700 overflow-hidden z-50">
                                        <div className="p-3 sm:p-4 border-b border-[#dfe0e2] dark:border-gray-700 flex justify-between items-center">
                                            <h3 className="font-bold text-sm sm:text-base text-[#131416] dark:text-white">Notifications</h3>
                                            {unreadCount > 0 && (
                                                <button
                                                    onClick={handleMarkAllRead}
                                                    className="text-xs text-primary font-medium hover:underline"
                                                >
                                                    Mark all read
                                                </button>
                                            )}
                                        </div>
                                        <div className="max-h-[70vh] sm:max-h-96 overflow-y-auto">
                                            {notifications.length === 0 ? (
                                                <div className="p-8 sm:p-12 text-center">
                                                    <span className="material-symbols-outlined text-4xl sm:text-5xl text-gray-300 dark:text-gray-700 mb-3">
                                                        notifications_off
                                                    </span>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
                                                </div>
                                            ) : (
                                                notifications.map(notification => (
                                                    <div
                                                        key={notification.id}
                                                        onClick={() => handleNotificationClick(notification)}
                                                        className={`p-3 sm:p-4 border-b border-[#dfe0e2] dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#23292f] transition-colors cursor-pointer ${!notification.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
                                                    >
                                                        <div className="flex gap-2 sm:gap-3">
                                                            <div className={`mt-1 min-w-[6px] h-1.5 rounded-full ${!notification.read ? 'bg-primary' : 'bg-transparent'}`}></div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs sm:text-sm font-semibold text-[#131416] dark:text-white mb-1 line-clamp-1">
                                                                    {notification.title}
                                                                </p>
                                                                <p className="text-[10px] sm:text-xs text-[#6e757c] dark:text-gray-400 mb-2 line-clamp-2">
                                                                    {notification.message}
                                                                </p>
                                                                <p className="text-[9px] sm:text-[10px] text-gray-400">
                                                                    {getTimeAgo(notification.created_at)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* User Profile Dropdown - Desktop */}
                            <div className="relative group hidden sm:block">
                                <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                                    <div className="w-8 h-8 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center">
                                        <span className="text-primary font-bold text-sm">
                                            {(user.full_name || user.fullName)?.charAt(0).toUpperCase() || 'U'}
                                        </span>
                                    </div>
                                </button>
                                {/* Dropdown Menu */}
                                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                                    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                                        <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                                            {user.full_name || user.fullName}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                            {user.email}
                                        </p>
                                    </div>
                                    <Link
                                        to="/profile"
                                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        Profile
                                    </Link>
                                    <Link
                                        to="/settings"
                                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        Settings
                                    </Link>
                                    <Link
                                        to="/feedback"
                                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        Send Feedback
                                    </Link>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg"
                                    >
                                        Logout
                                    </button>
                                </div>
                            </div>

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                                <span className="material-symbols-outlined text-gray-600 dark:text-gray-300">
                                    {mobileMenuOpen ? 'close' : 'menu'}
                                </span>
                            </button>
                        </>
                    ) : (
                        <>
                            {/* Mobile Menu Button for Non-Logged-In Users */}
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                                <span className="material-symbols-outlined text-gray-600 dark:text-gray-300">
                                    {mobileMenuOpen ? 'close' : 'menu'}
                                </span>
                            </button>

                            <Link
                                to="/signup"
                                className="hidden sm:flex min-w-[70px] sm:min-w-[84px] cursor-pointer items-center justify-center rounded-lg h-9 sm:h-10 px-3 sm:px-4 bg-primary text-white text-xs sm:text-sm font-bold shadow-sm shadow-primary/25 transition-all hover:bg-[#3d5872] hover:text-white"
                            >
                                Sign Up
                            </Link>
                            <Link
                                to="/login"
                                className="hidden sm:flex min-w-[70px] sm:min-w-[84px] cursor-pointer items-center justify-center rounded-lg h-9 sm:h-10 px-3 sm:px-4 bg-[#f1f2f3] dark:bg-gray-800 text-[#131416] dark:text-white text-xs sm:text-sm font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            >
                                Login
                            </Link>
                        </>
                    )}
                </div>
            </div>

            {/* Mobile Menu Overlay - For Logged In Users */}
            {user && mobileMenuOpen && (
                <div className="lg:hidden border-t border-[#dfe0e2] dark:border-gray-800 bg-white dark:bg-background-dark">
                    <nav className="px-4 py-3 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
                        {/* User Info - Mobile Only */}
                        <div className="sm:hidden p-3 mb-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center flex-shrink-0">
                                    <span className="text-primary font-bold">
                                        {(user.full_name || user.fullName)?.charAt(0).toUpperCase() || 'U'}
                                    </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                                        {user.full_name || user.fullName}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {user.email}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <Link
                            to="/dashboard"
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${isActive('/dashboard') ? 'font-bold text-primary bg-primary/5' : 'font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                        >
                            <span className="material-symbols-outlined text-xl">dashboard</span>
                            Dashboard
                        </Link>
                        <Link
                            to="/browse"
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${isActive('/browse') ? 'font-bold text-primary bg-primary/5' : 'font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                        >
                            <span className="material-symbols-outlined text-xl">search</span>
                            Browse Papers
                        </Link>
                        <Link
                            to="/upload"
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${isActive('/upload') ? 'font-bold text-primary bg-primary/5' : 'font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                        >
                            <span className="material-symbols-outlined text-xl">upload</span>
                            Upload
                        </Link>
                        <Link
                            to="/my-uploads"
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${isActive('/my-uploads') ? 'font-bold text-primary bg-primary/5' : 'font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                        >
                            <span className="material-symbols-outlined text-xl">folder</span>
                            My Uploads
                        </Link>
                        <Link
                            to="/backpack"
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${isActive('/backpack') ? 'font-bold text-primary bg-primary/5' : 'font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                        >
                            <span className="material-symbols-outlined text-xl">backpack</span>
                            Backpack
                        </Link>
                        {user.role === 'admin' && (
                            <Link
                                to="/admin/dashboard"
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${location.pathname.startsWith('/admin') ? 'font-bold text-primary bg-primary/5' : 'font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                            >
                                <span className="material-symbols-outlined text-xl">admin_panel_settings</span>
                                Admin
                            </Link>
                        )}

                        <div className="border-t border-[#dfe0e2] dark:border-gray-700 my-2"></div>

                        <Link
                            to="/profile"
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                            <span className="material-symbols-outlined text-xl">person</span>
                            Profile
                        </Link>
                        <Link
                            to="/settings"
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                            <span className="material-symbols-outlined text-xl">settings</span>
                            Settings
                        </Link>
                        <Link
                            to="/feedback"
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                            <span className="material-symbols-outlined text-xl">feedback</span>
                            Send Feedback
                        </Link>
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                        >
                            <span className="material-symbols-outlined text-xl">logout</span>
                            Logout
                        </button>
                    </nav>
                </div>
            )}

            {/* Mobile Menu Overlay - For Non-Logged-In Users */}
            {!user && mobileMenuOpen && (
                <div className="lg:hidden border-t border-[#dfe0e2] dark:border-gray-800 bg-white dark:bg-background-dark">
                    <nav className="px-4 py-3 space-y-1">
                        <Link
                            to="/about"
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${isActive('/about') ? 'font-bold text-primary bg-primary/5' : 'font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                        >
                            <span className="material-symbols-outlined text-xl">info</span>
                            About
                        </Link>

                        <div className="border-t border-[#dfe0e2] dark:border-gray-700 my-2"></div>

                        <Link
                            to="/signup"
                            className="flex items-center justify-center gap-2 w-full rounded-lg h-11 px-4 bg-primary text-white text-sm font-bold shadow-sm shadow-primary/25 transition-all hover:bg-[#3d5872] hover:text-white"
                        >
                            <span className="material-symbols-outlined text-xl">person_add</span>
                            Sign Up
                        </Link>
                        <Link
                            to="/login"
                            className="flex items-center justify-center gap-2 w-full rounded-lg h-11 px-4 bg-[#f1f2f3] dark:bg-gray-800 text-[#131416] dark:text-white text-sm font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        >
                            <span className="material-symbols-outlined text-xl">login</span>
                            Login
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Navbar;
